import React, { useCallback, useMemo } from "react";
import formatCurrency from "@tvg/formatter/currency";
import { get } from "lodash";
import { Saddle } from "../Saddle";
import { Icon } from "../Icon";
import { Paragraph } from "../Typography";
import { Tag } from "../Tag";
import {
  findCoupledRunners,
  rangeSelections,
  saddleRowQaLabel,
  getRunnerNameDetails
} from "./utils";
import type { SaddleSelectionsCompProps, Selection } from "./types";
import {
  SelectionsContainer,
  SaddleSelectionContainer,
  RowContainer,
  ColumnContainer,
  IconContainer,
  SaddleDetailsParagraph,
  AlternatePlaceholder,
  AlternateDivider
} from "./styled-components";

export const SaddleSelections = ({
  numWagerableRunners,
  selections,
  isDark,
  scratches,
  wagerDetails = false,
  shouldShowRefundTag,
  breed = "thoroughbred",
  qaLabel = "saddle-selections-container",
  onHandicaping,
  hideWillPaysTag = false,
  enableMyBetsBehavior = false,
  showOdds,
  favoriteRunner,
  showReplacedRunners,
  raceStatus,
  showAlternatePlaceholder = false
}: SaddleSelectionsCompProps) => {
  const saddleSelections =
    selections?.length > 0
      ? selections.sort((a, b) => {
          if (b.isAlternate !== a.isAlternate) {
            return b.isAlternate ? -1 : 1;
          }
          return (
            parseInt(a.number.toString(), 10) -
            parseInt(b.number.toString(), 10)
          );
        })
      : [];

  const alternateSelections = useMemo(
    () =>
      saddleSelections
        .filter((selection) => selection.isAlternate)
        .sort((a, b) => {
          const aScratched = scratches?.includes(a.number);
          const bScratched = scratches?.includes(b.number);

          if (aScratched === bScratched) return 0;
          return aScratched ? 1 : -1;
        }),
    [saddleSelections, scratches]
  );

  const hasScratches = scratches && scratches.length > 0;
  const hasWinner = saddleSelections.some((selection) => selection.winner);
  const hasWillPays = saddleSelections.some((selection) => selection.willPay);
  const isAlternatesScratched = alternateSelections.every((item) =>
    scratches?.includes(item.number)
  );

  const checkSaddleSelection = useCallback(
    (runnerNumber: string) =>
      saddleSelections.find(
        (saddleSelection) => saddleSelection.number === runnerNumber
      ),
    [saddleSelections, hasWinner, hasScratches, hasWillPays]
  );

  const replacedRunners = useMemo(() => {
    if (alternateSelections.length > 0 && !isAlternatesScratched) {
      return alternateSelections;
    }
    return [
      {
        ...favoriteRunner,
        number: get(favoriteRunner, "runnerNumber"),
        winner: get(favoriteRunner, "isWinner")
      } as Selection
    ];
  }, [alternateSelections, isAlternatesScratched, favoriteRunner]);

  const isAllSelections = !!(
    numWagerableRunners &&
    Number(numWagerableRunners) ===
      saddleSelections.length - alternateSelections.length &&
    !wagerDetails
  );

  const isSaddleNumber = !isAllSelections && saddleSelections.length > 0;

  const rangedSelections: string[] = useMemo(
    () =>
      rangeSelections(
        saddleSelections,
        scratches ?? [],
        wagerDetails,
        enableMyBetsBehavior,
        !!showReplacedRunners,
        !!(!hasWinner && !isSaddleNumber && !onHandicaping)
      )
        .join()
        .split(/(-)/)
        .join()
        .split(",")
        .filter((item) =>
          wagerDetails ? item !== "|" && item !== "" : item !== ""
        ),
    [
      wagerDetails,
      saddleSelections,
      scratches,
      showReplacedRunners,
      enableMyBetsBehavior,
      hasWinner,
      isSaddleNumber,
      onHandicaping
    ]
  );

  const isSaddleScratched = useCallback(
    (currentSelection: Selection) =>
      scratches?.includes(currentSelection.number) ||
      (!enableMyBetsBehavior &&
        hasWinner &&
        currentSelection?.winner === false),
    [hasWinner, scratches]
  );

  const isRaceFinished = useCallback(
    (currentSelection: Selection) => {
      const allScratched = saddleSelections.length === scratches?.length;
      const currentNotWinner = hasWinner && !currentSelection.winner;
      const myBetsRaceFinished =
        enableMyBetsBehavior &&
        (["lost", "win"].includes(raceStatus ?? "") ||
          (raceStatus === "attention" && !currentSelection.isAlternate));

      return allScratched || currentNotWinner || myBetsRaceFinished;
    },
    [saddleSelections, hasWinner, scratches, raceStatus, enableMyBetsBehavior]
  );

  return (
    <SelectionsContainer data-qa-label={qaLabel}>
      <SaddleSelectionContainer isDetailView={wagerDetails}>
        {rangedSelections.length === 0 && (
          <Saddle
            number="?"
            size="s"
            saddleColor="var(--fd-colors-core-white-tint-020)"
            numberColor="var(--fd-colors-core-white)"
            enableMyBetsBehavior={enableMyBetsBehavior}
          />
        )}
        {rangedSelections.map((item, index) => {
          const currentSaddleSelection = checkSaddleSelection(item);
          const coupledRunners = findCoupledRunners(saddleSelections, item);
          const runnerNameDetails = getRunnerNameDetails({
            item,
            scratches,
            showReplacedRunners,
            coupledRunners,
            replacedRunners,
            currentSaddleSelection
          });

          if (item === "|") {
            return <AlternateDivider isDark={isDark} />;
          }

          return (
            <RowContainer
              isDetailView={wagerDetails}
              showReplacedRunners={
                showReplacedRunners && scratches?.includes(item)
              }
              key={`sanddle-arrow-${index.toString()}`}
              data-qa-label={saddleRowQaLabel(
                item,
                currentSaddleSelection?.isAlternate
              )}
            >
              {item === "all" && (
                <Saddle
                  all={isAllSelections}
                  number="All"
                  size="s"
                  breed={breed}
                />
              )}

              {item === "-" && (
                <IconContainer isDetailView>
                  <Icon
                    size="m"
                    name="arrowRight"
                    lineColor={
                      isDark
                        ? "--fd-colors-content-on-dark"
                        : "--fd-colors-content-default"
                    }
                    qaLabel="runner-number-range-arrow"
                  />
                </IconContainer>
              )}

              {item === "?" && (
                <Saddle
                  number="?"
                  size="s"
                  numberColor="var(--fd-colors-core-white)"
                  saddleColor="var(--fd-colors-core-white-tint-020)"
                />
              )}

              {currentSaddleSelection && (
                <>
                  <ColumnContainer>
                    <Saddle
                      isScratched={isSaddleScratched(currentSaddleSelection)}
                      raceFinished={isRaceFinished(currentSaddleSelection)}
                      key={`Saddle-num-${currentSaddleSelection.number}`}
                      number={currentSaddleSelection.number}
                      numberColor={currentSaddleSelection.numberColor}
                      saddleColor={currentSaddleSelection.saddleColor}
                      all={currentSaddleSelection.number === "ALL"}
                      isWinner={
                        !currentSaddleSelection.isAlternate &&
                        !currentSaddleSelection.scratched &&
                        currentSaddleSelection.winner
                      }
                      breed={breed}
                      enableMyBetsBehavior={enableMyBetsBehavior}
                      size="s"
                      showAlternateIcon={currentSaddleSelection.isAlternate}
                    />

                    {runnerNameDetails.showReplacementName &&
                      enableMyBetsBehavior && (
                        <>
                          <IconContainer
                            key={`sanddle-arrow-${index.toString()}`}
                            isDetailView={wagerDetails}
                          >
                            <Icon
                              size="m"
                              name="arrowRight"
                              lineColor="--fd-colors-system-warning-background-default"
                              qaLabel="runner-number-range-arrow"
                            />
                          </IconContainer>
                          {replacedRunners
                            .filter((replaced) =>
                              !wagerDetails
                                ? true
                                : !scratches?.includes(replaced.number)
                            )
                            .map((replaced) => (
                              <Saddle
                                isScratched={isSaddleScratched(replaced)}
                                raceFinished={isRaceFinished(replaced)}
                                key={`Saddle-num-${replaced.number}`}
                                number={replaced.number}
                                numberColor={replaced.numberColor}
                                saddleColor={replaced.saddleColor}
                                enableMyBetsBehavior={enableMyBetsBehavior}
                                isWinner={replaced.winner}
                                breed={breed}
                                size="s"
                              />
                            ))}
                        </>
                      )}
                  </ColumnContainer>

                  {wagerDetails && (
                    <SaddleDetailsParagraph
                      qaLabel={`runner-name-${runnerNameDetails.displayName}`}
                      fontSize="s"
                      fontFamily={runnerNameDetails.fontFamily}
                      color={runnerNameDetails.color}
                      scratched={runnerNameDetails.scratched}
                      m={0}
                    >
                      {runnerNameDetails.displayName}
                    </SaddleDetailsParagraph>
                  )}

                  {wagerDetails && (
                    <ColumnContainer>
                      {!!currentSaddleSelection.willPay && !hideWillPaysTag && (
                        <Paragraph
                          qaLabel="details-will-pay-amount"
                          textAlign="center"
                          fontSize="s"
                          fontFamily="medium"
                          color={
                            currentSaddleSelection.isSettled
                              ? "--fd-colors-content-subtle"
                              : "--fd-colors-content-default"
                          }
                          m={0}
                        >
                          {formatCurrency(currentSaddleSelection.willPay)}
                        </Paragraph>
                      )}

                      {!!shouldShowRefundTag &&
                        !!currentSaddleSelection.scratched && (
                          <Tag label="REFUND" size="small" variant="warning" />
                        )}

                      {showOdds && runnerNameDetails.currentOdds && (
                        <Paragraph
                          qaLabel="details-odds"
                          textAlign="center"
                          fontSize="s"
                          fontFamily="bold"
                          m={0}
                        >
                          <>
                            {get(runnerNameDetails.currentOdds, "numerator")}
                            {get(runnerNameDetails.currentOdds, "denominator")
                              ? `/${get(
                                  runnerNameDetails.currentOdds,
                                  "denominator"
                                )}`
                              : ""}
                          </>
                        </Paragraph>
                      )}

                      {/* {showReplacedRunners &&
                      !currentSaddleSelection.isAlternate &&
                      !!currentSaddleSelection.scratched &&
                      replacedRunners.find(
                        (replaced) =>
                          replaced.number ===
                            get(favoriteRunner, "runnerNumber") &&
                          !replaced.isAlternate
                      ) && <Tag label="FAV" size="small" variant="favorite" />} */}
                    </ColumnContainer>
                  )}
                </>
              )}
            </RowContainer>
          );
        })}
      </SaddleSelectionContainer>
      {!wagerDetails &&
        showAlternatePlaceholder &&
        alternateSelections.length === 0 && (
          <AlternatePlaceholder>
            <AlternateDivider isDark={isDark} />
            <Saddle
              number="?"
              size="s"
              numberColor="var(--fd-colors-core-white)"
              saddleColor="var(--fd-colors-core-white-tint-020)"
              qaLabel="saddle-?-alternate"
              showAlternateIcon
            />
          </AlternatePlaceholder>
        )}
    </SelectionsContainer>
  );
};

export type { SaddleSelectionsCompProps, Selection };
